import React from "react";
import PropTypes from "prop-types";

/**
 * NeoInput
 * Componente de entrada de dados unificado e premium do NeoApp.
 * Suporta caixa de texto, números, senhas, datas, textareas, e seletores nativos.
 * Integra-se perfeitamente com Formik, exibindo erros de validação integrados.
 * Estilos e tamanhos isolados em NeoInput.css.
 */
const NeoInput = ({
    label = "",
    type = "text", // 'text' | 'number' | 'select' | 'textarea' | 'date' | 'password' | etc.
    value = "",
    onChange,
    onBlur,
    placeholder = "",
    disabled = false,
    readOnly = false,
    error = "", // Formik/Yup error message
    flat = false, // true = sem borda (bg-light), false = contornado chocolate
    size = "md", // 'sm' | 'md' | 'lg'
    options = [], // [{ value, label }] para selects
    icon = "", // Ícone MDI opcional (ex: 'mdi-email-outline')
    iconRight = false, // posiciona o ícone na direita se true
    className = "",
    style = {},
    inputStyle = {},
    children,
    ...props
}) => {
    const hasIcon = !!icon;
    const isInvalid = !!error;

    // Determina a classe do input
    const inputClass = [
        "form-control",
        "neo-input",
        flat ? "neo-input-flat" : "neo-input-contour",
        isInvalid ? "is-invalid" : ""
    ].filter(Boolean).join(" ");

    // Classes do wrapper principal
    const wrapperClass = [
        "neo-input-wrapper",
        `neo-input-wrapper-${size}`,
        hasIcon ? (iconRight ? "neo-input-has-icon-right" : "neo-input-has-icon-left") : "",
        className
    ].filter(Boolean).join(" ");

    // Renderiza o controle de input com base no tipo
    const renderInput = () => {
        if (type === "textarea") {
            return (
                <textarea
                    className={inputClass}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    style={inputStyle}
                    {...props}
                />
            );
        }

        if (type === "select") {
            return (
                <select
                    className={inputClass}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    style={inputStyle}
                    {...props}
                >
                    {placeholder && <option value="">{placeholder}</option>}
                    {options && options.map((opt, idx) => (
                        <option key={idx} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                    {children}
                </select>
            );
        }

        return (
            <input
                type={type}
                className={inputClass}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                style={inputStyle}
                {...props}
            />
        );
    };

    return (
        <div className={wrapperClass} style={style}>
            {/* Label do Input */}
            {label && <label className="neo-input-label">{label}</label>}

            {/* Input Container (Suporta Ícone Absoluto) */}
            <div className="neo-input-container">
                {/* Ícone Esquerdo */}
                {hasIcon && !iconRight && (
                    <i className={`mdi ${icon} neo-input-icon neo-input-icon-left`} />
                )}

                {/* Elemento de Input */}
                {renderInput()}

                {/* Ícone Direito */}
                {hasIcon && iconRight && (
                    <i className={`mdi ${icon} neo-input-icon neo-input-icon-right`} />
                )}
            </div>

            {/* Mensagem de Erro Integrada */}
            {isInvalid && <span className="neo-input-error-msg">{error}</span>}
        </div>
    );
};

NeoInput.propTypes = {
    label: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    error: PropTypes.string,
    flat: PropTypes.bool,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired
        })
    ),
    icon: PropTypes.string,
    iconRight: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    inputStyle: PropTypes.object
};

export default NeoInput;
